/**
 * The gallery's filter state, rewritten as the query string the API reads.
 *
 * Lives apart from the page because it is pure, because it is the single place every filter becomes
 * a request, and because it is where an empty AI filter once took the whole gallery down.
 */
const getFilterOptionValues = (value) => {
  if (Array.isArray(value))
    return value.map(item => item?.value).filter(item => item !== undefined && item !== null);

  if (value?.value !== undefined && value?.value !== null)
    return [value.value];

  return [];
};

export const createParams = (filters, { jailedProjectValues = [], viewingAsClient = false } = {}) => {
  const params = new URLSearchParams();
  const jailedProjectSet = new Set(jailedProjectValues.map(value => `${value}`));

  // Asks the server to answer as it would a client, so the preview does not keep listing the
  // admin-only photos a client never receives.
  if (viewingAsClient) params.append('as_client', 'true');

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'project' && jailedProjectValues.length > 0)
      return;

    if (key === 'createdat' && value) {
      params.append('createdat[startDate]', value.startDate);
      params.append('createdat[endDate]', value.endDate);
    } else if (key === 'favorite' || key === 'latest_visit') {
      if (value === true) {
        params.append(key, 'true');
      }
    } else if (key === 'deleted') {
      if (value === true) {
        params.append('deleted', 'true');
      }
    } else if (key === 'ai') {
      // Structured rather than one parameter per field: a brand and a price read off the same label
      // are one condition, and a flat bag cannot say so.
      //
      // Emitted only once it holds something: the panel signals « no condition » with null, and
      // serialising that sent the string « null », which the server read as a filter it could not
      // parse — so opening the section and emptying it again answered 400 to every photo.
      if (value && typeof value === 'object' && Object.keys(value).length > 0)
        params.append('ai', JSON.stringify(value));
    } else if (Array.isArray(value)) {
      value.forEach(v => {
        params.append(`${key}[]`, v.value);
      });
    } else if (value?.value) {
      params.append(key, value.value);
    }
  });

  if (jailedProjectValues.length > 0) {
    const selectedProjectValues = getFilterOptionValues(filters.project)
      .filter(value => jailedProjectSet.has(`${value}`));
    const projectValues = selectedProjectValues.length > 0 ? selectedProjectValues : jailedProjectValues;

    projectValues.forEach(value => {
      params.append('project[]', value);
    });
  }

  return params;
};

const FACET_IGNORED_PARAMS = [
  'ai', 'latest_visit',
  'product', 'product[]', 'product_item', 'product_item[]', 'product_ai', 'product_ai[]'
];

/**
 * The query string a dimension menu sends: the gallery's, minus the filters the facets cube never
 * compiles. They changed nothing in the answer, yet every AI filter change reloaded the menus.
 */
export const toFacetParams = (params) => {
  const facetParams = new URLSearchParams(params);
  FACET_IGNORED_PARAMS.forEach(name => facetParams.delete(name));
  return facetParams;
};

const mergeOptions = (...lists) => {
  const byValue = new Map();
  lists.flat().filter(Boolean).forEach(option => {
    if (!byValue.has(`${option.value}`)) byValue.set(`${option.value}`, { value: option.value, label: option.label ?? option.value });
  });
  return [...byValue.values()];
};

/**
 * Rewrite a `product` filter as its two sides, `product_item` and `product_ai`.
 *
 * `product` searches a code in the declared item and in what the AI read at once. The gallery shows
 * each side as its own filter, so that one can be dropped and the other kept: a search, a link or a
 * saved search still carrying `product` is opened as both. The AI side exists for administrators
 * only, as on the server.
 *
 * @param {object} filters the gallery's filter state
 * @param {{ isAdmin?: boolean }} [options]
 * @returns {object} the same filters, `product` replaced by its sides
 */
export const splitProductFilter = (filters, { isAdmin = true } = {}) => {
  if (!filters?.product) return filters;
  const { product, ...rest } = filters;
  const codes = Array.isArray(product) ? product : [product];
  const split = { ...rest, product_item: mergeOptions(rest.product_item, codes) };
  if (isAdmin) split.product_ai = mergeOptions(rest.product_ai, codes);
  return split;
};
